import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatChange, rangeToDates, type DashboardFilters } from '@/lib/admin/dashboardFilters';

type Counts = { papers: number; users: number; reviews: number; comments: number };
interface TrendsData {
  daily: Array<{ d: string; private_papers: number; public_papers: number; users: number; reviews: number; comments: number }>;
  current: Counts;
  previous: Counts;
}

const LABELS: Record<keyof Counts, string> = {
  papers: 'New papers', users: 'New users', reviews: 'New reviews', comments: 'New comments',
};

const ContentTrends: React.FC<{ filters: DashboardFilters }> = ({ filters }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-content-trends', filters.range],
    queryFn: async () => {
      const { from, to } = rangeToDates(filters.range);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)('admin_content_trends', { p_from: from, p_to: to });
      if (error) throw error;
      return data as TrendsData;
    },
    staleTime: 60_000,
  });

  if (error) return <p className="text-sm text-destructive">Could not load content trends.</p>;

  return (
    <div className="mb-8 space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {(Object.keys(LABELS) as (keyof Counts)[]).map((k) => (
          <Card key={k}>
            <CardHeader className="pb-1"><CardTitle className="text-sm font-medium text-muted-foreground">{LABELS[k]}</CardTitle></CardHeader>
            <CardContent>
              {isLoading || !data ? <Skeleton className="h-8 w-20" /> : (
                <>
                  <p className="text-3xl font-semibold tabular-nums">{Number(data.current[k]) || 0}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatChange(Number(data.current[k]) || 0, Number(data.previous[k]) || 0)}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">New papers per day</CardTitle></CardHeader>
        <CardContent className="h-64">
          {isLoading || !data ? <Skeleton className="h-full w-full" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="d" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="public_papers" name="Public" stackId="p" fill="hsl(var(--primary))" />
                <Bar dataKey="private_papers" name="Private" stackId="p" fill="hsl(var(--muted-foreground))" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContentTrends;
